import { CommonLocale, PhonogramManageFormLocale } from "@/locales/localeid";
import {
	PhonogramDeleteStatus,
	PhonogramDetailMedia,
	PhonogramDetailModel,
	PhonogramModel,
	PhonogramSaveMedia,
	PhonogramSaveModel,
	PhonogramType
} from "@/models/phonogram-manager-model";
import { VCSLevelModel } from "@/models/title-list-model";
import { MediaUploadState } from "@/modules/media-upload";
import { VersionGroupModule } from "@/modules/versiongroup";
import { PhonogramManagerService } from "@/services/phonogram-manager-service";
import { ResourceService } from "@/services/resource";
import { CurriculumType, MediaPlayerContentType, MediaSlot, MediaStatus, MediaType } from "@/util/enum";
import { Button, Col, Divider, FormModel, Icon, Input, Radio, Row, Select, Upload } from "ant-design-vue";
import { fmtMsg, MessageHelper, NotificationType, PrimaryLanguageLocale, ServerExceptionCode } from "commonui";
import { cloneDeep, isString, orderBy } from "lodash";
import { v4 } from "uuid";
import { Component, Prop, Ref, Vue, Watch } from "vue-property-decorator";
import { getModule } from "vuex-module-decorators";
import { Drawer } from "../drawer";
import Loading from "../loading/loading.vue";
import VideoJSPlayer from "../videojsplayer/videojsplayer.vue";
import IVideoJSPlayer from "../videojsplayer/videojsplayer";
import IPdfPagesViewer from "../pdf-pages-viewer/pdf-pages-viewer";
import PdfPagesViewer from "../pdf-pages-viewer/pdf-pages-viewer.vue";

@Component({
	components: {
		Drawer,
		Button,
		FormModel,
		FormItem: FormModel.Item,
		Input,
		Select,
		Option: Select.Option,
		Radio,
		RadioGroup: Radio.Group,
		Divider,
		Row,
		Col,
		Loading,
		Icon,
		Upload,
		VideoJSPlayer,
		PdfPagesViewer
	}
})
export default class PhonogramManagerForm extends Vue {
	@Ref("formRef") formRef!: FormModel;
	@Ref("playerRef") playerRef!: IVideoJSPlayer;
	@Ref("pdfPagesViewerRef") pdfPagesViewerRef!: IPdfPagesViewer;
	@Prop({ default: () => [] }) phonograms!: PhonogramModel[];
	@Prop({ default: () => [] }) levels!: VCSLevelModel[];

	public PhonogramType = PhonogramType;
	public MediaStatus = MediaStatus;
	public locale = PhonogramManageFormLocale;

	private mediaUpload = getModule(MediaUploadState);

	public visible: boolean = false;
	public loading: boolean = false;
	public saveLoading: boolean = false;
	public data: PhonogramSaveModel = this.getValues();
	public orderedPhonograms: PhonogramModel[] = [];

	public mediaSlotInfo: {
		[key: number]: {
			ext: string;
			icon: string;
			text: string;
			tooltip: string;
			handler: (item: PhonogramSaveMedia) => void;
		};
	} = {
		[MediaSlot.Audio]: {
			ext: ".mp3",
			icon: "audiotrack",
			text: this.$format(this.locale.MediaAudioLabel),
			tooltip: this.$format(this.locale.MediaAudioPlayTooltip),
			handler: this.playAudio
		},
		[MediaSlot.ViewingContent]: {
			ext: ".pdf",
			icon: "picture_as_pdf",
			text: this.$format(this.locale.MediaViewingContentLabel),
			tooltip: this.$format(this.locale.MediaViewingContentViewTooltip),
			handler: this.viewPages
		}
	};

	mounted() {
		this.orderPhonograms();
	}

	@Watch("phonograms", { deep: true })
	orderPhonograms() {
		this.orderedPhonograms = orderBy(this.phonograms, "name");
	}

	public get isPhonogramWordCard(): boolean {
		return this.data.phonogramTypeId === PhonogramType.WordCard;
	}
	public get isPhonogramCard(): boolean {
		return this.data.phonogramTypeId === PhonogramType.Card;
	}
	public get isEdit(): boolean {
		return !!this.data.id;
	}
	public get modalTitle(): string {
		if (this.isEdit) {
			return this.$format(this.locale.TitleEdit);
		}
		return this.$format(this.locale.TitleNew);
	}

	private getValues(phonogram?: PhonogramDetailModel): PhonogramSaveModel {
		return {
			id: phonogram?.id || undefined,
			name: phonogram?.name || "",
			focusUnitId: phonogram?.focusUnitId || null,
			maxReviewUnitId: phonogram?.maxReviewUnitId || null,
			phonogramTypeId: phonogram?.phonogramTypeId || PhonogramType.Card,
			relatedPhonogramId: phonogram?.relatedPhonogramId || null,
			uploadedFiles: this.formatMediaSlots(phonogram?.uploadedFiles)
		};
	}

	private formatMediaSlots(files?: PhonogramDetailMedia[]): [PhonogramSaveMedia, PhonogramSaveMedia] {
		const audioSlot: PhonogramSaveMedia = {
			mediaSlotId: MediaSlot.Audio,
			id: v4(),
			uploadedFilePath: null,
			audioLength: 0,
			size: 0,
			isRemoved: false,
			isModified: false,
			isAdded: false
		};
		const viewingContentSlot: PhonogramSaveMedia = {
			mediaSlotId: MediaSlot.ViewingContent,
			id: v4(),
			uploadedFilePath: null,
			size: 0,
			isRemoved: false,
			isModified: false,
			isAdded: false
		};
		if (files) {
			files.forEach((file) => {
				if (file.mediaSlotId === MediaSlot.Audio) {
					audioSlot.uploadedFilePath = file.filePath;
					audioSlot.id = file.mediaId;
					audioSlot.mediaStatusId = file.mediaStatusId;
				} else if (file.mediaSlotId === MediaSlot.ViewingContent) {
					viewingContentSlot.uploadedFilePath = file.filePath;
					viewingContentSlot.id = file.mediaId;
					viewingContentSlot.mediaStatusId = file.mediaStatusId;
				}
			});
		}
		return [viewingContentSlot, audioSlot];
	}

	private checkMediaInitialState() {
		const hasViewingContentMedia = this.data.uploadedFiles.some(
			(media) => media.mediaSlotId === MediaSlot.ViewingContent && media.uploadedFilePath !== null
		);
		const hasAudioMedia = this.data.uploadedFiles.some(
			(media) => media.mediaSlotId === MediaSlot.Audio && media.uploadedFilePath !== null
		);

		this.data.hasViewingContentMedia = hasViewingContentMedia;
		this.data.hasAudioMedia = hasAudioMedia;
	}

	public open(phonogram?: PhonogramModel) {
		this.visible = true;
		if (phonogram) {
			this.loading = true;
			PhonogramManagerService.getPhonogramDetail({
				id: phonogram.id,
				phonogramType: phonogram.phonogramTypeId
			})
				.then((phonogramDetail) => {
					this.loading = false;
					this.data = this.getValues(phonogramDetail);
					this.checkMediaInitialState();
				})
				.catch(() => {
					this.loading = false;
				});
		} else {
			this.data = this.getValues(phonogram);
		}
	}

	public close() {
		this.visible = false;
	}

	public isFile(uploadedFilePath: File | string | null): boolean {
		return uploadedFilePath instanceof File;
	}

	public getFileName(uploadedFilePath: string | File | null): string {
		if (uploadedFilePath instanceof File) {
			return uploadedFilePath.name;
		}
		return "";
	}

	public addMedia($event: { file: File }, item: PhonogramSaveMedia) {
		const { file } = $event;

		if (file && this.validateFile(item, file)) {
			const isViewingContent = item.mediaSlotId === MediaSlot.ViewingContent;
			const isAudioContent = item.mediaSlotId === MediaSlot.Audio;

			const isMediaInitiallyPresent = isViewingContent
				? this.data.hasViewingContentMedia
				: isAudioContent
				? this.data.hasAudioMedia
				: false;

			if (item.isRemoved) {
				if (isMediaInitiallyPresent) {
					item.isModified = true;
					item.isAdded = false;
				} else {
					item.isAdded = true;
					item.isModified = false;
				}
				item.isRemoved = false;
			} else if (item.isModified || (isString(item.uploadedFilePath) && !item.isAdded)) {
				item.isModified = true;
				item.isAdded = false;
			} else {
				item.isAdded = true;
				item.isModified = false;
			}
			item.isRemoved = false;
			item.uploadedFilePath = file;
		}
	}

	public onFileReject(item: PhonogramSaveMedia) {
		const expectedExtension = this.mediaSlotInfo[item.mediaSlotId].ext;
		MessageHelper.Message(
			NotificationType.Warning,
			this.$format(this.locale.CurrentMediaInvalidMessage, { extension: expectedExtension })
		);
	}

	public onFocusUnitChange() {
		if (this.data.phonogramTypeId === PhonogramType.WordCard) {
			this.data.relatedPhonogramId = null;
		}
	}

	private validateFile(item: PhonogramSaveMedia, file: File): boolean {
		const fileExtension = "." + file.name.split(".").pop();

		const expectedExtension = this.mediaSlotInfo[item.mediaSlotId].ext;
		if (fileExtension === expectedExtension) {
			return true;
		}

		this.onFileReject(item);

		return false;
	}

	public removeMedia(item: PhonogramSaveMedia) {
		// Check if it an existing item
		if (item.id) {
			item.isRemoved = true;
			item.isAdded = false;
			item.isModified = false;
		}

		item.uploadedFilePath = null;
	}

	public viewPages(item: PhonogramSaveMedia) {
		if (isString(item.uploadedFilePath) && item.id) {
			this.pdfPagesViewerRef.open(
				{
					mediaId: item.id,
					mediaSlotId: item.mediaSlotId
				},
				this.data
			);
		}
	}

	private playAudio(item: PhonogramSaveMedia) {
		if (isString(item.uploadedFilePath) && item.id) {
			ResourceService.getDownloadMediaUrl(item.id).then((mediaUrl) => {
				const result = this.playerRef.showModal(item.id!, MediaType.MP3, "Media Viewer");
				if (result) {
					setTimeout(() => {
						this.playerRef.setAWSMediaPlayer(item.id!, mediaUrl, MediaPlayerContentType.mp3);
					}, 100);
				}
			});
		}
	}

	private getVersionGroupIdOfUnit(unitId: number) {
		const unit = this.levels.find((level) => level.id === unitId)!;
		if (unit.curriculumType === CurriculumType.GrapeSEED) {
			return VersionGroupModule.selectedGsVersion;
		}
		return VersionGroupModule.selectedLsVersion;
	}

	private getLengthOfAudioFile(file: File): Promise<number> {
		return new Promise((resolve, reject) => {
			const url = URL.createObjectURL(file);
			const audio = new Audio(url);
			audio.onloadedmetadata = () => {
				resolve(audio.duration);
			};
			audio.onerror = () => {
				reject();
			};
		});
	}

	private uploadFiles(): Promise<void> {
		return new Promise((resolve, reject) => {
			const mediaFilesToUpload = this.data.uploadedFiles.filter(
				(media) => media.uploadedFilePath instanceof File
			);
			if (mediaFilesToUpload.length) {
				Promise.all(
					mediaFilesToUpload.map((media) => {
						const id = v4();
						return this.mediaUpload
							.uploadFile({
								id,
								file: media.uploadedFilePath as File,
								slotId: media.mediaSlotId,
								languageCode: PrimaryLanguageLocale.English
							})
							.then(async () => {
								if (media.mediaSlotId === MediaSlot.Audio) {
									const audioLength = await this.getLengthOfAudioFile(media.uploadedFilePath as File);
									media.audioLength = audioLength;
								}

								const uploadedFile = this.mediaUpload.fileUploads.find((file) => file.id === id)!;
								media.uploadedFilePath = uploadedFile.filePath;
								media.size = uploadedFile.size;
								return media;
							});
					})
				)
					.then(() => {
						resolve();
					})
					.catch(() => {
						reject("File upload error...");
					});
			} else {
				resolve();
			}
		});
	}

	private getDataForSave() {
		const newData = cloneDeep(this.data);

		// Removing maxReviewUnitId for Phonogram Card
		if (newData.phonogramTypeId !== PhonogramType.Card) {
			delete newData.maxReviewUnitId;
		}

		// Removing relatedPhonogramId for Phonogram Word Card
		if (newData.phonogramTypeId !== PhonogramType.WordCard) {
			delete newData.relatedPhonogramId;
		}

		// Adding version group ID respective to the focus unit
		newData.versionGroupId = this.getVersionGroupIdOfUnit(newData.focusUnitId!);

		// Formatting the media array
		newData.uploadedFiles.forEach((file) => {
			if (file.isRemoved) {
				delete file.uploadedFilePath;
			}
			delete file.mediaStatusId;
		});
		return newData;
	}

	public save() {
		this.formRef.validate((isValid) => {
			if (isValid) {
				this.saveLoading = true;
				this.uploadFiles()
					.then(() => {
						const data = this.getDataForSave();
						PhonogramManagerService.save(data)
							.then(() => {
								if (this.data.id) {
									MessageHelper.Message(
										NotificationType.Success,
										fmtMsg(this.locale.PhonogramEditedSuccessfullyMessage)
									);
								} else {
									MessageHelper.Message(
										NotificationType.Success,
										fmtMsg(this.locale.PhonogramAddedSuccessfullyMessage)
									);
								}
								this.saveLoading = false;
								this.$emit("reload");
								this.close();
							})
							.catch((error) => {
								if (error?.body?.error_code) {
									this.showErrorMessage({ error_code: error.body.error_code });
								} else if (error?.error_code) {
									this.showErrorMessage({ error_code: error.error_code });
								}
								this.saveLoading = false;
							});
					})
					.catch((err) => {
						console.error(err);
						this.saveLoading = false;
					});
			}
		});
	}

	private showErrorMessage({ error_code }) {
		switch (error_code) {
			case ServerExceptionCode.NoSpecialDesignationException:
				MessageHelper.Message(
					NotificationType.Failed,
					this.$format(CommonLocale.CommonSpecialDesignationNotExist)
				);
				break;
		}
	}

	public deletePhonogram() {
		this.$confirm({
			title: this.$format(this.locale.DeleteConfirmationTitle),
			content: this.$format(this.locale.DeleteConfirmationText, { name: this.data.name }),
			okText: this.$format(this.locale.DeleteConfirmationYes),
			cancelText: this.$format(this.locale.DeleteConfirmationCancel),
			onOk: () => {
				return PhonogramManagerService.remove({
					id: this.data.id!,
					phonogramType: this.data.phonogramTypeId
				}).then((status) => {
					switch (status) {
						case PhonogramDeleteStatus.PCReferenceInPWCExistExceptionCode:
							MessageHelper.Message(
								NotificationType.Warning,
								this.$format(this.locale.DeleteConfirmationReferenceErrorMsg)
							);
							break;
						case PhonogramDeleteStatus.PCRelatedItemReferenceExistExceptionCode:
							MessageHelper.Message(
								NotificationType.Warning,
								this.$format(this.locale.DeleteConfirmationPCReferenceInComponentMsg)
							);
							break;
						case PhonogramDeleteStatus.PWCRelatedItemReferenceExistExceptionCode:
							MessageHelper.Message(
								NotificationType.Warning,
								this.$format(this.locale.DeleteConfirmationPWCReferenceInComponentMsg)
							);
							break;
						default:
							MessageHelper.Message(
								NotificationType.Success,
								fmtMsg(this.locale.PhonogramDeletedSuccessfullyMessage)
							);
							this.close();
							this.$emit("reload");
					}
				});
			}
		});
	}
}
